/**
 * PricingPage – Pro upgrade page with PayPal checkout.
 *
 * Flow:
 *   1. Fetch /api/billing/plans → get plans metadata + PayPal client id.
 *   2. User picks a plan card (monthly / annual / lifetime).
 *   3. Optional: user enters a promo code. If it's a "full" code, the backend
 *      short-circuits and grants Lifetime for free — no PayPal popup.
 *   4. Otherwise, PayPal Smart Buttons render. On approve we capture the
 *      order server-side and flip the user's plan.
 *
 * IMPORTANT: the PayPal script is loaded via @paypal/react-paypal-js, gated by
 * the CLIENT-ID we fetch from /api/billing/plans — so it matches whatever
 * env (sandbox / live) the backend is configured for.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CaretLeft, CheckCircle, Crown, Sparkle, Ticket, SpinnerGap, Info,
} from '@phosphor-icons/react';
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { http, apiErrorMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { subscriptionsAllowedInApp, webUpgradeUrl, isNative } from '@/lib/native';

const PLAN_META = {
  monthly:  { badge: 'Flexible',    perks: ['Unlimited sessions of every type', 'All 9 voices', 'Scaffold mode + CEFR tracking', 'Auto-renews monthly — cancel anytime'] },
  annual:   { badge: 'Save 33%',    perks: ['Everything in Monthly', 'Best price / minute practiced', 'Auto-renews yearly — cancel anytime'] },
  lifetime: { badge: 'One-time',    perks: ['Everything forever', 'No recurring charges', 'Future features included'] },
};

// Tailored "here's why you landed on /pricing" banners. Each flow that
// can hit a 3-per-day free cap redirects with a ?reason=... query param
// matching one of these keys. Copy is warm and non-punitive — the cap
// is framed as a finish-line ("you finished today's free set!"),
// not a wall, to preserve the momentum that drives conversion.
const PAYWALL_REASONS = {
  teacher_daily_cap: {
    headline: "You've finished today's 3 free Teacher lessons 👏",
    body:
      'Momentum is the #1 thing that turns new words into fluency. ' +
      "Keep it going with unlimited lessons, full voice library, and CEFR " +
      'progress tracking — cancel anytime.',
  },
  free_talk_daily_cap: {
    headline: "You've used today's 3 free Free-Talk sessions 🎤",
    body:
      'The more you speak, the faster you lose the accent. Unlimited ' +
      'Free-Talk + all 9 voices is waiting whenever you are.',
  },
  scenario_daily_cap: {
    headline: "You've finished today's 3 free Scenarios 🎭",
    body:
      'Real-life role-plays (restaurant, airport, interview…) are how ' +
      'most learners graduate from textbook Spanish to actually being ' +
      'understood. Upgrade for unlimited.',
  },
  repeat_daily_cap: {
    headline: "You've used today's 3 free Repeat Practice drills 🔁",
    body:
      'Pronunciation lives in repetition. Unlimited drills + all voices ' +
      "make the difference — cancel anytime.",
  },
  default: {
    headline: "You've reached today's free quota 🎉",
    body:
      "You're making real progress. Upgrade to Pro for unlimited access " +
      'across every flow — cancel anytime.',
  },
};

export default function PricingPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Some flows (Teacher, Free-Talk, Scenarios, Repeat) redirect here
  // with ?reason=<kind>_daily_cap when the user hits their 3/day cap —
  // look up a tailored banner in ``PAYWALL_REASONS`` above.
  const paywallReason = searchParams.get('reason');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [clientId, setClientId] = useState('');
  const [selected, setSelected] = useState('annual');
  const [promoCode, setPromoCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  // Resolved PayPal subscription plan IDs per kind — we fetch them up-front
  // so PayPal Buttons can render the Subscribe flow WITHOUT waiting for the
  // user's click (PayPal needs the plan_id at mount time, not click time).
  const [subPlanIds, setSubPlanIds] = useState({});  // {monthly:'P-...', annual:'P-...'}

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get('/billing/plans');
        setPlans(data.plans || []);
        setCurrent(data.current);
        setClientId(data.paypal_client_id || '');
      } catch (e) {
        setError(apiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Lazy-fetch the subscription plan-id for the currently-selected plan. We
  // cache per-kind so flipping between monthly/annual is instant after both
  // have been resolved once.
  useEffect(() => {
    if (selected === 'lifetime') return;
    if (subPlanIds[selected]) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.post('/billing/create-subscription', { plan_kind: selected });
        if (!cancelled && data?.plan_id) {
          setSubPlanIds((p) => ({ ...p, [selected]: data.plan_id }));
        }
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e));
      }
    })();
    return () => { cancelled = true; };
  }, [selected, subPlanIds]);

  const priceFor = (kind) => plans.find((p) => p.kind === kind)?.price ?? 0;
  const discountedPrice = useMemo(
    () => Math.max(0.50, Number((priceFor(selected) * (100 - appliedDiscount) / 100).toFixed(2))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, appliedDiscount, plans]
  );

  /** Try to redeem a `full` (100% free) code without PayPal. */
  async function tryFreeRedeem() {
    if (!promoCode.trim()) { setError('Enter a code first'); return; }
    setBusy(true);
    setError('');
    try {
      const { data } = await http.post('/promo/redeem', { code: promoCode.trim().toUpperCase() });
      await refresh();
      alert(`🎉 You're now on ${data.plan_kind}. Enjoy!`);
      navigate('/home');
    } catch (e) {
      const msg = apiErrorMessage(e);
      if (e?.response?.status === 400 && /discount/i.test(msg)) {
        // Percent code — apply locally and let user proceed to PayPal.
        try {
          const { data } = await http.post('/billing/create-order', { plan_kind: selected, promo_code: promoCode.trim().toUpperCase() });
          setAppliedDiscount(data.discount_pct || 0);
          setError('');
          alert(`Promo applied: ${data.discount_pct}% off. Pay with PayPal below.`);
        } catch (err) {
          setError(apiErrorMessage(err));
        }
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="lf-shell flex h-screen items-center justify-center">
        <SpinnerGap size={28} className="animate-spin text-[var(--lf-primary)]" />
      </div>
    );
  }

  const isPro = current?.is_pro;

  return (
    <div className="lf-shell px-6 py-8" data-testid="pricing-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]" data-testid="pricing-back">
          <CaretLeft size={18} />
        </button>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Upgrade</div>
          <h1 className="text-2xl font-bold">Go Pro</h1>
        </div>
      </div>

      {/* Paywall-reason banner. Whenever a flow hits the free 3/day cap
          and redirects to /pricing with ?reason=..., we show a warm,
          non-punitive message at the top so the user knows WHY they
          landed here and what unlocks if they upgrade. Copy varies per
          reason so it feels contextual instead of generic. */}
      {!isPro && paywallReason ? (() => {
        const copy = PAYWALL_REASONS[paywallReason] || PAYWALL_REASONS.default;
        return (
          <div
            className="lf-card mt-5 border-[var(--lf-primary)] bg-[#FFF5EE] p-4"
            data-testid="pricing-paywall-banner"
          >
            <div className="text-sm font-semibold text-[var(--lf-text)]">
              {copy.headline}
            </div>
            <p className="mt-1 text-xs text-[var(--lf-text-muted)]">
              {copy.body}
            </p>
          </div>
        );
      })() : null}

      {isPro ? (
        <div className="lf-card mt-5 p-5" data-testid="pricing-already-pro">
          <div className="inline-flex items-center gap-1 lf-chip lf-chip-primary">
            <Crown size={12} weight="fill" /> {current.label}
          </div>
          <div className="mt-2 text-lg font-bold">You're already on Pro 🎉</div>
          <div className="text-sm text-[var(--lf-text-muted)]">
            {current.plan_expires_at ? `Renews or expires on ${new Date(current.plan_expires_at).toLocaleDateString()}` : 'Lifetime access. No renewals.'}
          </div>
          <Link to="/home" className="lf-btn lf-btn-primary mt-4 w-full">Back to Home</Link>
        </div>
      ) : null}

      <p className="mt-5 text-sm text-[var(--lf-text-muted)]">
        Free gives you <b>2 Free-Talk sessions</b> (3 min each) and <b>1 Repeat Practice</b> per day.
        Pro removes the limits and unlocks every feature.
      </p>

      {/* Plan cards */}
      <div className="mt-6 space-y-3" data-testid="pricing-plans">
        {plans.map((p) => {
          const meta = PLAN_META[p.kind] || {};
          const active = selected === p.kind;
          const isLifetime = p.kind === 'lifetime';
          return (
            <motion.button
              key={p.kind}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => { setSelected(p.kind); setAppliedDiscount(0); }}
              className={`lf-card w-full p-5 text-left transition-all ${active ? 'ring-2 ring-[var(--lf-primary)]' : ''}`}
              data-testid={`pricing-plan-${p.kind}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-bold">{p.label}</div>
                    {meta.badge ? <span className="lf-chip lf-chip-accent">{meta.badge}</span> : null}
                    {isLifetime ? <Crown size={14} weight="fill" className="text-[var(--lf-primary)]" /> : null}
                  </div>
                  <div className="mt-0.5 text-2xl font-extrabold">
                    ${p.price.toFixed(2)}
                    <span className="ml-1 text-sm font-medium text-[var(--lf-text-muted)]">
                      {p.kind === 'monthly' ? '/ month' : p.kind === 'annual' ? '/ year' : 'one-time'}
                    </span>
                  </div>
                </div>
                {active ? <CheckCircle size={22} weight="fill" className="text-[var(--lf-primary)]" /> : null}
              </div>
              <ul className="mt-3 space-y-1">
                {(meta.perks || []).map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-xs text-[var(--lf-text)]">
                    <Sparkle size={12} weight="fill" className="mt-0.5 text-[var(--lf-primary)]" />
                    {perk}
                  </li>
                ))}
              </ul>
            </motion.button>
          );
        })}
      </div>

      {/* Promo code */}
      <div className="lf-card mt-5 p-4" data-testid="pricing-promo-card">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Ticket size={16} weight="duotone" className="text-[var(--lf-primary)]" />
          Have a promo code?
        </div>
        <div className="mt-2 flex gap-2">
          <label htmlFor="pricing-promo" className="lf-sr-only">Promo code</label>
          <input
            id="pricing-promo"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            placeholder="LINGO-XXXXXX"
            className="lf-input flex-1 text-sm"
            data-testid="pricing-promo-input"
            autoComplete="off"
            aria-describedby="pricing-promo-help"
          />
          <button
            onClick={tryFreeRedeem}
            disabled={busy}
            className="lf-btn lf-btn-secondary text-sm"
            data-testid="pricing-promo-apply"
          >
            {busy ? <SpinnerGap size={14} className="animate-spin" /> : 'Apply'}
          </button>
        </div>
        {appliedDiscount > 0 ? (
          <div className="mt-2 inline-flex items-center gap-1 lf-chip lf-chip-success">
            <CheckCircle size={12} weight="fill" /> {appliedDiscount}% off applied
          </div>
        ) : null}
      </div>

      {/* Summary + PayPal */}
      <div className="lf-card mt-5 p-5" data-testid="pricing-summary">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">You'll pay</div>
            <div className="text-3xl font-extrabold">${discountedPrice.toFixed(2)}</div>
            {appliedDiscount > 0 ? (
              <div className="text-xs text-[var(--lf-text-muted)] line-through">${priceFor(selected).toFixed(2)}</div>
            ) : null}
          </div>
          <div className="text-right text-xs text-[var(--lf-text-muted)]">
            <div>USD</div>
            <div className="mt-1 inline-flex items-center gap-1">
              <Info size={10} /> Sandbox test mode
            </div>
          </div>
        </div>

        {clientId && !isPro && subscriptionsAllowedInApp() ? (
          <div className="mt-4" data-testid="pricing-paypal-buttons">
            {/* Single PayPalScriptProvider for the whole page. The inner
                <PayPalFlow/> uses usePayPalScriptReducer to dispatch
                `resetOptions` whenever the learner switches between
                subscription plans and the one-time Lifetime purchase — this
                is the officially-supported pattern for swapping intents
                without unmounting the provider (which causes SDK load races). */}
            <PayPalScriptProvider
              options={{
                'client-id': clientId,
                currency: 'USD',
                intent: 'subscription',
                vault: true,
                components: 'buttons',
              }}
            >
              <PayPalFlow
                selected={selected}
                subPlanIds={subPlanIds}
                promoCode={promoCode}
                appliedDiscount={appliedDiscount}
                setAppliedDiscount={setAppliedDiscount}
                setError={setError}
                setCapturing={setCapturing}
                refresh={refresh}
                navigate={navigate}
              />
            </PayPalScriptProvider>
            {capturing ? (
              <div className="mt-2 text-xs text-[var(--lf-text-muted)] text-center">
                <SpinnerGap size={12} className="inline animate-spin" /> Finalizing your plan…
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Reader-app mode: on native iOS/Android we CANNOT show PayPal for
            digital subscriptions (App Store rules). Instead we route the
            learner to the web to complete the purchase. */}
        {!isPro && !subscriptionsAllowedInApp() ? (
          <div className="mt-4 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface-muted)] p-4" data-testid="pricing-native-upgrade">
            <div className="text-sm font-bold">Subscribe on the web</div>
            <p className="mt-1 text-xs text-[var(--lf-text-muted)]">
              Because of App Store policies, Pro plans are sold on our website.
              Subscribe once on any browser — this app will automatically show
              your Pro features as soon as you sign in here.
            </p>
            <button
              onClick={async () => {
                try {
                  if (isNative()) {
                    const { Browser } = await import('@capacitor/browser');
                    await Browser.open({ url: webUpgradeUrl() });
                    return;
                  }
                  window.open(webUpgradeUrl(), '_blank', 'noopener');
                } catch (e) { setError(String(e?.message || e)); }
              }}
              className="lf-btn lf-btn-primary mt-3 w-full"
              data-testid="pricing-open-web-btn"
            >
              Open web checkout
            </button>
          </div>
        ) : null}

        {error ? <div className="mt-3 text-sm text-[var(--lf-destructive)]" data-testid="pricing-error">{error}</div> : null}
      </div>

      <div className="mt-6 text-xs text-[var(--lf-text-muted)] text-center">
        <p>Payments handled by PayPal. Sandbox means no real money will be charged.</p>
      </div>

      <div className="h-10" />
    </div>
  );
}


/**
 * PayPalFlow – the inside of PayPalScriptProvider. Uses the official
 * `usePayPalScriptReducer` hook to swap the SDK intent between
 * 'subscription' (monthly/annual) and 'capture' (lifetime one-time) WITHOUT
 * unmounting the provider (which caused persistent SDK load races).
 */
function PayPalFlow({
  selected, subPlanIds, promoCode, appliedDiscount,
  setAppliedDiscount, setError, setCapturing, refresh, navigate,
}) {
  const [{ options, isResolved, isPending }, dispatch] = usePayPalScriptReducer();

  // Swap the SDK's intent whenever the learner toggles between a recurring
  // subscription plan and the one-time Lifetime purchase.
  useEffect(() => {
    const desiredIntent = selected === 'lifetime' ? 'capture' : 'subscription';
    const desiredVault = selected !== 'lifetime';
    if (options.intent !== desiredIntent || options.vault !== desiredVault) {
      dispatch({
        type: 'resetOptions',
        value: { ...options, intent: desiredIntent, vault: desiredVault },
      });
    }
  }, [selected, options, dispatch]);

  // While the SDK is reloading (after an intent swap) OR we're still waiting
  // for the subscription plan_id from the backend, show a loading placeholder
  // inside the same container so the checkout area never goes blank.
  const desiredIntent = selected === 'lifetime' ? 'capture' : 'subscription';
  const intentReady = options.intent === desiredIntent && isResolved;
  const waitingForPlanId = selected !== 'lifetime' && !subPlanIds[selected];

  if (!intentReady || waitingForPlanId || isPending) {
    return (
      <div
        className="rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface-muted)] px-4 py-6 text-center text-sm text-[var(--lf-text-muted)]"
        data-testid="pricing-paypal-loading"
      >
        <SpinnerGap size={16} className="inline animate-spin" /> Loading PayPal checkout…
      </div>
    );
  }

  if (selected === 'lifetime') {
    return (
      <PayPalButtons
        key={`pay-lifetime-${appliedDiscount}`}
        style={{ layout: 'vertical', shape: 'pill', color: 'gold', label: 'pay' }}
        createOrder={async () => {
          setError('');
          try {
            const { data } = await http.post('/billing/create-order', {
              plan_kind: 'lifetime',
              promo_code: promoCode.trim().toUpperCase() || undefined,
            });
            if (data.granted) { await refresh(); navigate('/home'); return ''; }
            setAppliedDiscount(data.discount_pct || 0);
            return data.order_id;
          } catch (e) { setError(apiErrorMessage(e)); throw e; }
        }}
        onApprove={async (data) => {
          setCapturing(true);
          try {
            await http.post('/billing/capture-order', {
              order_id: data.orderID, plan_kind: 'lifetime',
            });
            await refresh(); navigate('/home');
          } catch (e) { setError(apiErrorMessage(e)); }
          finally { setCapturing(false); }
        }}
        onError={(err) => setError(String(err?.message || err))}
        onCancel={() => setError('Checkout cancelled')}
      />
    );
  }

  // monthly / annual subscription flow
  return (
    <PayPalButtons
      key={`sub-${selected}-${subPlanIds[selected]}`}
      style={{ layout: 'vertical', shape: 'pill', color: 'gold', label: 'subscribe' }}
      createSubscription={(_data, actions) =>
        actions.subscription.create({ plan_id: subPlanIds[selected] })
      }
      onApprove={async (data) => {
        setCapturing(true);
        try {
          await http.post('/billing/activate-subscription', {
            subscription_id: data.subscriptionID,
            plan_kind: selected,
          });
          await refresh();
          navigate('/home');
        } catch (e) { setError(apiErrorMessage(e)); }
        finally { setCapturing(false); }
      }}
      onError={(err) => setError(String(err?.message || err))}
      onCancel={() => setError('Subscription cancelled')}
    />
  );
}
